"use client";
import React, { useState, useEffect } from "react";

import { motion } from "motion/react";
import { cn } from "@/lib/utils";

// Aceternity UI — Hover Border Gradient. Ported verbatim (source is already
// TypeScript, so no type porting needed) — only this comment was added.

type Direction = "TOP" | "LEFT" | "BOTTOM" | "RIGHT";

export function HoverBorderGradient({
  children,
  containerClassName,
  className,
  as: Tag = "button",
  duration = 1,
  clockwise = true,
  // For decorative, non-interactive uses (a banner, not a button) — the
  // beam just keeps rotating, hover never pauses it, never swaps in the
  // hover-only highlight burst, and the container never darkens/lightens.
  staticBeam = false,
  ...props
}: React.PropsWithChildren<
  {
    as?: React.ElementType;
    containerClassName?: string;
    className?: string;
    duration?: number;
    clockwise?: boolean;
    staticBeam?: boolean;
  } & React.HTMLAttributes<HTMLElement>
>) {
  const [hovered, setHovered] = useState<boolean>(false);
  const [direction, setDirection] = useState<Direction>("TOP");

  const rotateDirection = (currentDirection: Direction): Direction => {
    const directions: Direction[] = ["TOP", "LEFT", "BOTTOM", "RIGHT"];
    const currentIndex = directions.indexOf(currentDirection);
    const nextIndex = clockwise
      ? (currentIndex - 1 + directions.length) % directions.length
      : (currentIndex + 1) % directions.length;
    return directions[nextIndex];
  };

  // Was Aceternity's default white (hsl(0,0%,100%)) — this is the shimmer
  // that's visible continuously, not just on hover, so it needed to actually
  // be green, not just the hover burst below. Last pass only recolored
  // `highlight` (hover-only) and left this white, which is what was still
  // showing up as a white border at rest.
  const movingMap: Record<Direction, string> = {
    TOP: "radial-gradient(20.7% 50% at 50% 0%, #5cd06f 0%, rgba(60, 180, 79, 0) 100%)",
    LEFT: "radial-gradient(16.6% 43.1% at 0% 50%, #5cd06f 0%, rgba(60, 180, 79, 0) 100%)",
    BOTTOM:
      "radial-gradient(20.7% 50% at 50% 100%, #5cd06f 0%, rgba(60, 180, 79, 0) 100%)",
    RIGHT:
      "radial-gradient(16.2% 41.199999999999996% at 100% 50%, #5cd06f 0%, rgba(60, 180, 79, 0) 100%)",
  };

  // Was Aceternity's default blue (#3275F8) — swapped for the brand green so
  // the hover burst matches the rest of the page instead of clashing with it.
  const highlight =
    "radial-gradient(75% 181.15942028985506% at 50% 50%, #5cd06f 0%, rgba(60, 180, 79, 0) 100%)";

  useEffect(() => {
    if (staticBeam || !hovered) {
      const interval = setInterval(() => {
        setDirection((prevState) => rotateDirection(prevState));
      }, duration * 1000);
      return () => clearInterval(interval);
    }
  }, [hovered, staticBeam]);
  return (
    <Tag
      onMouseEnter={() => {
        if (!staticBeam) setHovered(true);
      }}
      onMouseLeave={() => {
        if (!staticBeam) setHovered(false);
      }}
      className={cn(
        // border-transparent — not just `border` — matters here: Tailwind
        // v4's unspecified border color is `currentColor`, which resolves
        // to white in this dark-text context. That was a real, solid,
        // static white ring sitting on top of the animated gradient the
        // whole time, not the "moving beam" it looked like it should be.
        //
        // dark:bg-white/20 is gone outright (not just gated behind
        // staticBeam): this site is always dark regardless of OS
        // preference, so that variant firing on a dark-mode OS was adding
        // a translucent white wash across the whole container — the other
        // real source of the "whitish border" report, on top of the
        // border-color bug above.
        "relative flex rounded-full border border-white/10 content-center bg-black/20 transition duration-500 items-center flex-col flex-nowrap gap-10 h-min justify-center overflow-visible p-px decoration-clone w-fit",
        !staticBeam && "hover:bg-black/10",
        containerClassName
      )}
      {...props}
    >
      <div
        className={cn(
          "w-auto text-white cursor-pointer z-10 bg-black px-4 py-2 rounded-[inherit]",
          className
        )}
      >
        {children}
      </div>
      <motion.div
        className={cn(
          "flex-none inset-0 overflow-hidden absolute z-0 rounded-[inherit]"
        )}
        style={{
          filter: "blur(2px)",
          position: "absolute",
          width: "100%",
          height: "100%",
        }}
        initial={{ background: movingMap[direction] }}
        animate={{
          background:
            !staticBeam && hovered
              ? [movingMap[direction], highlight]
              : movingMap[direction],
        }}
        transition={{ ease: "linear", duration: duration ?? 1 }}
      />
      {/* Two real bugs here, both invisible on the original small pill
          buttons this was written for, both visible on a wide, gently-
          rounded banner:
          - `z-1` isn't a real Tailwind class (the numeric z-index scale is
            0/10/20/.../50) — it silently generated no z-index at all.
          - `rounded-[100px]` hardcodes "basically a full pill," which is
            true for a short button but not for a wide container using a
            smaller explicit radius (our rounded-[28px] banner) — the
            backing layer ended up a different, rounder shape than the
            outer ring it's supposed to mask, so their curves didn't line
            up at the corners. rounded-[inherit] matches whatever the
            actual container shape is, same as the content div already does. */}
      <div className="bg-black absolute z-[1] flex-none inset-[2px] rounded-[inherit]" />
    </Tag>
  );
}
